import Card from './ui/Card';

interface PropertySummary {
  address: string;
  role: 'seller' | 'buyer';
  tenure: 'freehold' | 'leasehold';
}

interface ConfidenceSummaryProps {
  property: PropertySummary;
  documentsUploaded: number;
  formsToGenerate: string[];
  /** Step to navigate to when "Edit documents" is clicked. */
  uploadsStep?: number;
  onEditStep?: (step: number) => void;
  /** Optional handler for the property-details "Edit" link (typically routes
   *  back to the property file). When omitted, the link is hidden. */
  onEditProperty?: () => void;
}

export default function ConfidenceSummary({
  property,
  documentsUploaded,
  formsToGenerate,
  uploadsStep = 2,
  onEditStep,
  onEditProperty,
}: ConfidenceSummaryProps) {
  return (
    <Card>
      <h3 className="text-lg font-semibold text-slate-950 mb-4">Summary</h3>

      {/* Property Details */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-slate-700">Property details</h4>
          {onEditProperty && (
            <button onClick={onEditProperty} className="text-xs text-blue-700 hover:text-blue-800 hover:underline">
              Edit
            </button>
          )}
        </div>
        <div className="bg-slate-50 rounded-lg p-3 space-y-1">
          <p className="text-sm text-slate-900">{property.address}</p>
          <p className="text-sm text-slate-600">
            {property.role === 'seller' ? 'Selling' : 'Buying'} • {property.tenure === 'freehold' ? 'Freehold' : 'Leasehold'}
          </p>
        </div>
      </div>

      {/* Documents Uploaded */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-slate-700">Documents uploaded</h4>
          {onEditStep && (
            <button onClick={() => onEditStep(uploadsStep)} className="text-xs text-blue-700 hover:text-blue-800 hover:underline">
              Edit
            </button>
          )}
        </div>
        <div className="bg-slate-50 rounded-lg p-3">
          <p className="text-sm text-slate-900">
            {documentsUploaded === 0
              ? 'No documents uploaded — forms based on your answers only'
              : `${documentsUploaded} document${documentsUploaded !== 1 ? 's' : ''} uploaded`}
          </p>
        </div>
      </div>

      {/* Forms to Generate */}
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-slate-700 mb-2">Forms to be generated</h4>
        <div className="bg-slate-50 rounded-lg p-3">
          <ul className="text-sm text-slate-900 space-y-1">
            {formsToGenerate.map((form, index) => (
              <li key={index} className="flex items-center gap-2">
                <svg className="w-4 h-4 text-emerald-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {form}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Reassurance Text */}
      <div className="flex items-start gap-2 p-3 bg-emerald-50 rounded-lg border border-emerald-100">
        <svg className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
        <p className="text-sm text-emerald-800">
          {documentsUploaded > 0
            ? 'Answers traceable to your questionnaire responses and uploaded documents.'
            : 'Answers based on your questionnaire responses. Upload documents to strengthen provenance.'}
        </p>
      </div>
    </Card>
  );
}

export type { PropertySummary, ConfidenceSummaryProps };
